<div class="container-box shadow-none">
  <div class="d-flex gap-3 align-items-center p-3">
    <img src="/assets/images/channel-wizards/listing-icon1.webp" alt="" height="40" />
    <div>
      <h6 class="mb-0" i18n>Connecting</h6>
      <div class="text-sm">
        <ng-container i18n>This section will prepare you for connecting Booking.com with AdvanceCM.</ng-container>
        <br />
        <ng-container i18n>Please read through these instructions carefully before proceeding.</ng-container>
      </div>
    </div>
  </div>
  <div class="border-top p-4 d-flex gap-5">
    <div class="flex-fill">
      <div *ngIf="isProcessing && autoStep" class="alert alert-secondary-info d-flex align-items-center gap-2">
        <i class="far fa-spinner fa-spin"></i>
        <ng-container *ngIf="autoStep === 'import'">
          <ng-container *ngIf="importProgressText; else importingDefault">{{ importProgressText }}</ng-container>
          <ng-template #importingDefault
            ><ng-container i18n>Importing your bookings from Booking.com…</ng-container></ng-template
          >
        </ng-container>
        <ng-container *ngIf="autoStep === 'availability'" i18n
          >Waiting for your availability to reach Booking.com…</ng-container
        >
      </div>
      <div *ngIf="autoChainFailed" class="alert alert-secondary-danger d-flex align-items-center gap-3">
        <div class="flex-fill" i18n>
          Automatic setup could not complete. You can retry, or run the remaining steps below manually.
        </div>
        <button class="btn btn-secondary-danger flex-shrink-0" (click)="onRetryAutoChain()" i18n>Retry</button>
      </div>
      <mat-accordion>
        <mat-expansion-panel [expanded]="true" #linkProperty>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">1</div>
              <ng-container i18n>Link your property</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isLinked"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              The next step in the process will be to link an existing AdvanceCM rental to your booking.com room ID.
            </p>

            <p i18n class="text-semi-bold">Please take note of the following:</p>

            <p i18n>
              A Booking.com property ID may include more than one room ID. These rooms should have their own rates and
              availability calendar and not be listed as inventory available on the same property ID. You can confirm
              this by checking your rates and availability calendar on booking.com and ensuring that the Rooms to Sell
              section shows either 1 or 0 but never more than 1.
            </p>
            <p i18n>
              If your property shows more than 1 room available within a property ID on the same calendar you will need
              to contact your booking.com account manager to have them split these rentals into their own rates
              calendars.
            </p>
            <div *ngIf="isLinked && !isRentalRatesReady" class="alert alert-secondary-warning2">
              <div i18n>Your rental's rate information is incomplete. You can complete it on the Rates step:</div>
              <div *ngFor="let item of ratesMissing">{{ item }}</div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <mat-form-field2 class="w-50" [showLabel]="false">
                <app-select
                  [formControl]="rentalIdCtrl"
                  [items]="rentals$ | async"
                  bindLabel="name"
                  bindValue="id"
                  [multiple]="false"
                  [required]="true"
                  [searchable]="true"
                  [disabled]="!!connection.rentalId"
                  placeholder="Select Rental to Link"
                  i18n-placeholder
                >
                </app-select>
              </mat-form-field2>
              <div class="flex-fill"></div>
              <button
                class="btn btn-light-info"
                [disabled]="rentalIdCtrl.invalid || !!connection.rentalId"
                (click)="onLink()"
              >
                <i class="cs-icon cs-icon-link"></i> <ng-container i18n>Link</ng-container>
              </button>
              <button class="btn btn-light-info" *ngIf="connection.rentalId" (click)="onRates()">
                <i class="far fa-calculator"></i> <ng-container i18n>Edit Rates</ng-container>
              </button>
              <button class="btn btn-light-info" (click)="importBookings.open()" [disabled]="!isLinked">
                <ng-container i18n>Next</ng-container> <i class="cs-icon cs-icon-arrow-down-long"></i>
              </button>
            </div>
          </div>
        </mat-expansion-panel>
        <mat-expansion-panel #importBookings [disabled]="!isLinked">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">2</div>
              <ng-container i18n>Import Bookings</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isBookingImported"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              Now that AdvanceCM is connected to booking.com you will need to import your bookings. Please note that
              AdvanceCM can only import current and future bookings. Only limited historical information is provided
              when importing from Booking.com.
            </p>

            <p i18n>
              Future bookings which were created in the past will be imported but bookings in which the departure date
              has already passed will not be.
            </p>

            <p i18n>
              The import starts automatically once your rental is linked. Use the Import button below if you want to run
              it again.
            </p>
            <div
              class="alert mt-3"
              *ngIf="importProgressText"
              [class.alert-secondary-info]="importStatus?.status !== 'failed' && !hasImportWarnings"
              [class.alert-secondary-warning2]="importStatus?.status === 'failed' || hasImportWarnings"
            >
              {{ importProgressText }}
            </div>
            <div class="d-flex ali-items-center gap-3">
              <button class="btn btn-light-info" (click)="onImport()" [disabled]="isProcessing">
                <i class="cs-icon cs-icon-download"></i> <ng-container i18n>Import</ng-container>
              </button>
              <div class="flex-fill"></div>
              <button class="btn btn-light-info" (click)="pushAvailability.open()" [disabled]="!isBookingImported">
                <ng-container i18n>Next</ng-container> <i class="cs-icon cs-icon-arrow-down-long"></i>
              </button>
            </div>
          </div>
        </mat-expansion-panel>
        <mat-expansion-panel #pushAvailability [disabled]="!isLinked || !isBookingImported">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">3</div>
              <ng-container i18n>Push Availability</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isAvailabilityPushed"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              This step pushes the availability that shows on your AdvanceCM calendar to Booking.com. It runs
              automatically after your bookings are imported, and you can re-run it with the button below at any time.
            </p>

            <p i18n>
              Push your availability only after your bookings have been imported: the availability sent to Booking.com
              is calculated from the bookings and hold events on your AdvanceCM calendar.
            </p>
            <div class="d-flex ali-items-center gap-3">
              <button class="btn btn-light-info" (click)="onPushAvailability()" [disabled]="isProcessing">
                <i class="cs-icon cs-icon-upload"></i> <ng-container i18n>Push Availability</ng-container>
              </button>
              <div class="flex-fill"></div>
            </div>
          </div>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  </div>
</div>
<div class="mt-3 d-flex align-items-center justify-content-end">
  <button
    class="btn btn-secondary-info"
    [disabled]="isProcessing || !isLinked || !isBookingImported || !isAvailabilityPushed"
    (click)="next.emit()"
  >
    <ng-container i18n>Next</ng-container>
    <i class="far fa-spin fa-spinner" *ngIf="isProcessing"></i>
    <i class="cs-icon cs-icon-arrow-right-long text-xxs" *ngIf="!isProcessing"></i>
  </button>
</div>
